import React from 'react';
import { NavSection, NavItem } from './types';
import { AppSidebarItem } from './app-sidebar-item';
import { isRouteActive } from './utils/route-matcher';
import { cn } from '../../../utils';
import { ANIMATION_DURATION } from './constants';

interface AppSidebarSectionProps {
  section?: NavSection;
  items?: NavItem[];
  label?: string;
  isCollapsed: boolean;
  pathname: string;
  showBorder?: boolean;
  className?: string;
  paddingTop?: string | { collapsed: string; expanded: string };
  onNavigate?: (href: string, isExternal: boolean) => void;
  isExtended?: boolean;
}

export function AppSidebarSection({
  section,
  items: itemsProp,
  label: labelProp,
  isCollapsed,
  pathname,
  showBorder = false,
  className,
  paddingTop,
  onNavigate,
  isExtended = false,
}: AppSidebarSectionProps) {
  // Use section if provided, otherwise use items/label props
  const items = section?.items ?? itemsProp ?? [];
  const label = section?.label ?? labelProp;

  const containerStyle = paddingTop
    ? typeof paddingTop === 'string'
      ? { paddingTop }
      : { paddingTop: isCollapsed ? paddingTop.collapsed : paddingTop.expanded }
    : undefined;

  const containerTransition =
    paddingTop && typeof paddingTop === 'object'
      ? `padding-top ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`
      : undefined;

  return (
    <div
      className={cn('tw-w-full', showBorder && 'tw-border-t', className)}
      style={{
        ...containerStyle,
        margin: 0,
        padding: 0,
        boxSizing: 'border-box' as const,
        transition: containerTransition,
        ...(showBorder && { borderTopColor: '#e5e7eb' }),
      }}
    >
      {label && (
        <div
          className={cn(
            'tw-font-bold tw-uppercase tw-tracking-wider tw-w-full',
            isCollapsed && 'tw-opacity-0 tw-invisible'
          )}
          style={{
            fontSize: '14px',
            fontWeight: 700,
            color: '#1f1f1f',
            lineHeight: 1,
            margin: 0,
            marginBottom: '4px',
            paddingTop: '4px',
            paddingBottom: '4px',
            paddingLeft: '12px',
            paddingRight: '12px',
            transition: `opacity ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1), visibility ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
          }}
        >
          {label}
        </div>
      )}
      <div
        className="tw-w-full"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          margin: 0,
          padding: 0,
        }}
      >
        {items.map((item) => {
          const isActive = isRouteActive(
            pathname,
            item.href,
            item.activeMatcher
          );
          return (
            <AppSidebarItem
              key={item.href}
              item={item}
              isActive={isActive}
              isCollapsed={isCollapsed}
              onNavigate={onNavigate}
              isExtended={isExtended}
            />
          );
        })}
      </div>
    </div>
  );
}
